import * as React from "react";
import { Button, Grid, Stack } from "@mui/material";
import styled from "styled-components";
import { useActiveIntegrations } from "src/scenes/dashboard/active-integrations-provider";
import IntegratedNoData from "src/scenes/dashboard/integrated-no-data";
import IntegratedNoResponse from "src/scenes/dashboard/integrated-no-response";
import KnowledgebaseIconLink from "src/components/knowledgebase-icon-link";
import printDateRange from "src/components/print-daterange";
import { useAuth } from "src/components/providers/auth-context";
import SettingsIcon from "@mui/icons-material/Settings";
import DeploymentsConfigModal from "src/scenes/dashboard/work-analytics/deployments-config/deployments-config-modal";
import InvalidIntegrationButton from "src/components/invalid-integration-button";
import Template from "src/components/template/template";
import fetchStatus from "src/utils/api/fetch-status";
import { Link as RouteLink, useParams } from "react-router-dom";
import { useTemplateViewEdit } from "src/components/providers/template-view-edit-provider";
import TemplateTitle from "src/components/template/template-title";
import TemplateRemove from "src/components/template/template-remove";
import TemplateAdd from "src/components/template/template-add";
import BorderClearIcon from "@mui/icons-material/BorderClear";
import BackspaceIcon from "@mui/icons-material/Backspace";
import SaveIcon from "@mui/icons-material/Save";
import TemplateItemEditDrawer from "src/components/template/template-item-edit-drawer";
import { getIsDashboardNotIntegrated } from "src/utils/is-not-integrated";
import NotIntegrated from "src/scenes/dashboard/not-integrated";
import ViewListIcon from "@mui/icons-material/ViewList";

const TemplateHelper = styled.p`
    font-size: 12px;
    font-style: normal;
    font-weight: 200;
    opacity: 0.7;
    margin: 0;
`;

const DashboardButton = styled(Button)`
    background-color: ${({ theme }) => theme.palette.background.default};
    font-size: 12px;
    margin-top: 13px;
    z-index: 10;
`;

const ToolbarButton = styled(Button)`
    font-size: 12px;
    width: 100%;
    z-index: 10;
`;

const HelperMessage = "All comparative data are calculated weekly";

const Analytics = () => {
    const { dashboardId } = useParams();
    const [toolsScrapping, setToolsScrapping] = React.useState([]);
    const [templateNoData, setTemplateNoData] = React.useState({});
    const [earliestFirstScrapped, setEarliestFirstScrapped] = React.useState<{
        [id: string]: string;
    }>({});
    const [apiStatus, setApiStatus] = React.useState<{
        [id: string]: { status: fetchStatus | null; errorCode: number | null };
    }>({});
    const [deploymentsConfigModal, setDeploymentsConfigModal] = React.useState(false);

    const { activeIntegrations, isIntegrated } = useActiveIntegrations();

    const { isDemo } = useAuth();

    const { dashboard, saveChanges, discardChanges, isDirty, onEditMode, setOnEditMode } =
        useTemplateViewEdit();

    React.useEffect(() => {
        if (activeIntegrations.status !== "PENDING") {
            const filterIntegrationStatus = () => {
                const { integrations } = activeIntegrations.data || [];
                const scrappingIntegrations = integrations
                    .filter((integration) => integration.status === "scrapping")
                    .map((integration) => integration.name);

                return scrappingIntegrations;
            };
            setToolsScrapping(filterIntegrationStatus);
        }
    }, [activeIntegrations.status]);

    const notIntegrated = React.useMemo(
        () => getIsDashboardNotIntegrated(dashboard, isIntegrated),
        [dashboard && dashboard.templates, isIntegrated]
    );

    if (notIntegrated) {
        return <NotIntegrated name={dashboardId?.split("-")[0]} />;
    }

    if (
        Object.values(templateNoData).length > 0 &&
        Object.values(templateNoData).length === dashboard?.templates?.length &&
        Object.values(templateNoData).filter((v) => v).length ===
            Object.values(templateNoData).length
    ) {
        return (
            <IntegratedNoData
                firstScrappedDate={Math.min(
                    ...Object.values(earliestFirstScrapped).map((d) => new Date(d).valueOf())
                )}
            />
        );
    }

    if (
        Object.values(apiStatus).length > 0 &&
        Object.values(apiStatus).length === dashboard?.templates?.length &&
        Object.values(apiStatus).filter((v) => v.status === fetchStatus.ERROR).length ===
            Object.values(apiStatus).length
    ) {
        return <IntegratedNoResponse />;
    }

    if (!dashboard) return null;

    return (
        <Stack spacing={3} mt={2} mb={4}>
            {onEditMode ? (
                <Stack direction="row" justifyContent="flex-start" style={{ width: "100%" }}>
                    <Button
                        startIcon={<BackspaceIcon />}
                        onClick={() => {
                            setOnEditMode(false);
                            discardChanges();
                        }}
                    >
                        {isDirty ? "Discard" : "Cancel"}
                    </Button>
                    <Button
                        disabled={!isDirty}
                        startIcon={<SaveIcon />}
                        onClick={() => {
                            setOnEditMode(false);
                            saveChanges({ users: "me" });
                        }}
                    >
                        Save
                    </Button>
                </Stack>
            ) : (
                <Grid container spacing={1}>
                    <Grid item md={12}>
                        <ToolbarButton
                            startIcon={<BorderClearIcon />}
                            onClick={() => setOnEditMode(true)}
                        >
                            Edit mode
                        </ToolbarButton>
                    </Grid>
                </Grid>
            )}
            {(dashboard?.templates || []).map((template, templateIndex) => {
                return (
                    <React.Fragment key={template.id}>
                        {onEditMode && <TemplateAdd templateIndex={templateIndex} />}
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            style={{ width: "100%" }}
                        >
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <TemplateTitle
                                    text={template.title.value}
                                    templateIndex={templateIndex}
                                />
                                <KnowledgebaseIconLink
                                    name={template.knowledgeBaseName}
                                    markdownId={null}
                                />
                                <TemplateHelper>{printDateRange()}</TemplateHelper>
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={2}>
                                {onEditMode ? (
                                    <TemplateRemove templateIndex={templateIndex} />
                                ) : (
                                    <TemplateHelper>{HelperMessage}</TemplateHelper>
                                )}
                                {template.configurationAction &&
                                    template.configurationAction.type ===
                                        "deploymentConfiguration" && (
                                        <DashboardButton
                                            variant="outlined"
                                            color="secondary"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setDeploymentsConfigModal(true);
                                            }}
                                            startIcon={<SettingsIcon />}
                                            style={{
                                                textDecoration: "auto",
                                                textAlign: "center",
                                                paddingRight: "12px",
                                            }}
                                        >
                                            Configuration
                                        </DashboardButton>
                                    )}
                                {template.configurationAction &&
                                    template.configurationAction.type ===
                                        "indexesConfiguration" && (
                                        <Button
                                            component={RouteLink}
                                            to="/indexes-settings"
                                            variant="contained"
                                            startIcon={<ViewListIcon />}
                                            style={{ width: "257px", fontSize: "12px" }}
                                        >
                                            Configure Indexes
                                        </Button>
                                    )}
                                <InvalidIntegrationButton
                                    dependencies={template.dependencies}
                                    isTeams={false}
                                />
                            </Stack>
                        </Stack>
                        <Template
                            viewType="user"
                            onEditMode={onEditMode}
                            toolsScrapping={toolsScrapping}
                            template={template}
                            templateIndex={templateIndex}
                            defaultDateRange={dashboard.defaultDateRange}
                            setTemplateNoData={(noData) =>
                                setTemplateNoData((prevState) => ({
                                    ...prevState,
                                    [template.id]: noData,
                                }))
                            }
                            setEarliestFirstScrapped={(fsd) =>
                                setEarliestFirstScrapped((prevState) => ({
                                    ...prevState,
                                    [template.id]: fsd,
                                }))
                            }
                            setApiStatus={(status, errorCode) =>
                                setApiStatus((prevState) => ({
                                    ...prevState,
                                    [template.id]: { status, errorCode },
                                }))
                            }
                        />
                    </React.Fragment>
                );
            })}
            {onEditMode && <TemplateAdd templateIndex={dashboard.templates.length} />}
            <DeploymentsConfigModal
                open={deploymentsConfigModal}
                isDemo={isDemo}
                handleClose={() => setDeploymentsConfigModal(!deploymentsConfigModal)}
                fetchAction={undefined}
                isTeams={false}
                teamId={undefined}
            />
            <TemplateItemEditDrawer />
        </Stack>
    );
};

export default Analytics;
